import { InteractionStatus, Logger } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { isAxiosError } from 'axios';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { USER_LOGIN } from 'watchtower-ui/api/api';
import { useCustomNavigate } from 'watchtower-ui/components/common/CustomNavigation';
import { FullScreenLoader } from 'watchtower-ui/components/common/Spinner';
import { useFeatureFlag } from 'watchtower-ui/contexts/FeatureFlagContext';
import type { CountryAccess } from 'watchtower-ui/pages/LandingPage/NavBar/types';
import { AVAILABLE_COUNTRIES } from 'watchtower-ui/types/country';
import { normalizeCountryCode } from 'watchtower-ui/utils/country';

export interface User {
  name: string;
  user_id: number;
  createdby: string;
  status: string;
  isadmin: boolean;
  email: string;
  reason: string | null;
  countries: CountryAccess[];
}

export interface LoginResponse {
  token: string;
  user: User;
}

/** @internal */
export interface AuthContextType {
  loginResponse: LoginResponse | null;
  loggedIn: boolean;
  countries: CountryAccess[];
  validCountries: string[];
  refreshLogin?: () => Promise<void>;
}

const AVAILABLE_COUNTRIES_FALLBACK: CountryAccess[] = AVAILABLE_COUNTRIES.map((code) => ({
  country_code: code,
  status: 'DENIED',
}));

export const AuthContext = createContext<AuthContextType>({
  loginResponse: null,
  loggedIn: false,
  countries: [],
  validCountries: [],
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { accounts, instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const loginCalled = useRef(false);

  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');

  const [countries, setCountries] = useState<CountryAccess[]>([]);
  const [loginResponse, setLoginResponse] = useState<LoginResponse | null>(null);

  const loggedIn = useMemo(
    () => accounts.length > 0 && !!loginResponse && isAuthenticated,
    [accounts.length, loginResponse, isAuthenticated],
  );

  const msalDetails = useMemo(
    () => ({ instance, inProgress, accounts, logger: new Logger({ piiLoggingEnabled: false }) }),
    [instance, inProgress, accounts],
  );

  const validCountries = useMemo(
    () => countries.filter((c) => c.status === 'APPROVED').map((c) => c.country_code),
    [countries],
  );

  const refreshLogin = useCallback(async () => {
    try {
      const resp = await USER_LOGIN(msalDetails, navigate, enableDeveloperLogs);
      const availableCountries = resp.user.countries || AVAILABLE_COUNTRIES_FALLBACK;

      const countryAccess = availableCountries.map((c) => ({
        status: c.status,
        country_code: normalizeCountryCode(c.country_code),
        role: c.role,
      }));

      setCountries(Array.from(countryAccess).sort((a, b) => a.country_code.localeCompare(b.country_code)));
      setLoginResponse(resp);
    } catch (err) {
      console.error('Login failed:', err);

      // Check if user not found (403) - redirect to request-access
      if (isAxiosError(err) && err.response?.status === 403) {
        // Extract country code from current URL path if present
        const pathParts = globalThis.location.pathname.split('/').filter(Boolean);
        const countryCode = pathParts.find((part) => part.length === 2)?.toUpperCase() || '';

        if (countryCode) {
          navigate({ to: '/request-access', removeCountry: true, searchParam: `country=${countryCode}` });
        } else {
          navigate({ to: '/request-access', removeCountry: true });
        }
        return;
      }

      // Other errors - go back to country selection
      navigate({ to: '/', removeCountry: true });
    }
  }, [msalDetails, navigate, enableDeveloperLogs]);

  useEffect(() => {
    if (
      inProgress === InteractionStatus.None && // MSAL is idle
      accounts.length > 0 && // we've got an account
      !loginCalled.current // haven't asked yet
    ) {
      loginCalled.current = true;
      /* eslint-disable-next-line @typescript-eslint/no-floating-promises -- refreshLogin is async */
      refreshLogin();
    }
  }, [inProgress, accounts, refreshLogin]);

  const value = useMemo(
    () => ({
      loginResponse,
      loggedIn,
      countries,
      validCountries,
      refreshLogin,
    }),
    [loginResponse, loggedIn, countries, validCountries, refreshLogin],
  );

  if (!loggedIn) {
    return <FullScreenLoader full />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
};
